/**
 * Who is allowed in, decided BEFORE the app mounts.
 *
 * Three outcomes matter and they are not the same thing:
 *
 *   ok            — signed in and entitled. Straight into the product, with
 *                   no extra screen. This is the daily-driver path and it
 *                   must not get slower or gain a click.
 *   not-entitled  — signed in, but not in the app-access group. During a soft
 *                   launch this is the COMMON case: staff on managed devices
 *                   are already signed into Entra, so they sail through login
 *                   and then hit AppAccessMiddleware's 403. Today that is a
 *                   dead end with nothing to do about it.
 *   signed-out    — no Entra session at all.
 *
 * The distinction between the last two is why this uses ssoSilent rather than
 * "is there a cached account". A first visit from an entitled user has no
 * cached account either, and showing THEM a sign-up page would be a
 * regression. ssoSilent asks "is there a session we can use", which is the
 * actual question. Only when that fails is the visitor genuinely signed out.
 */
import { ref } from "vue";

import {
  initMsal,
  currentAccount,
  ssoFailureReason,
  trySilentSso,
  USE_REAL_AUTH,
} from "./msal";

export type AccessState = "checking" | "ok" | "signed-out" | "not-entitled";
/** What determineAccess() can actually answer. "checking" is the initial
 *  value of the ref, never a result — saying so in the type is what lets the
 *  caller hand the answer straight to AccessGate without a cast. */
export type ResolvedAccess = Exclude<AccessState, "checking">;

export const accessState = ref<AccessState>("checking");
/** Shown on the not-entitled screen so the visitor knows which account the
 *  answer applies to — the usual cause is being signed in as the wrong one. */
export const signedInAs = ref<string>("");
export const signedInName = ref<string>("");
/** Why the gate is being shown, beyond the state itself. Sent with a beta
 *  signup so the first week's ambiguity — every request arriving as
 *  "signed-out" — can actually be read next time. */
export const gateReason = ref<string>("");

const API_BASE = import.meta.env.VITE_API_BASE ?? "";

/**
 * Ask an endpoint that sits BEHIND the entitlement gate whether we are past
 * it. `/api/assistants/visible` is the natural probe: it is what the hub
 * loads first anyway, so an entitled user pays nothing for this.
 *
 * Fails OPEN. A network blip, a cold start, a 500 — none of those mean "not
 * entitled", and treating them that way would show the sign-up page to
 * someone who has access, which is a far worse failure than briefly letting
 * an unentitled user see the hub and get a 403 from the next call.
 */
async function isEntitled(token: string): Promise<boolean> {
  try {
    const r = await fetch(`${API_BASE}/api/assistants/visible`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return r.status !== 403;
  } catch {
    return true;
  }
}

export async function determineAccess(): Promise<ResolvedAccess> {
  // Dev builds identify via the X-Dev-User picker; there is no Entra session
  // to reason about and no gate to be outside of.
  if (!USE_REAL_AUTH) {
    accessState.value = "ok";
    return accessState.value;
  }

  let app;
  try {
    app = await initMsal();
  } catch {
    // MSAL could not even initialise. Let the app mount and use its existing
    // error paths rather than replacing the product with a sign-up page.
    accessState.value = "ok";
    return accessState.value;
  }
  if (!app) {
    accessState.value = "ok";
    return accessState.value;
  }

  let account = currentAccount();
  if (!account) account = await trySilentSso();
  if (!account) {
    gateReason.value = ssoFailureReason() ?? "no_session";
    accessState.value = "signed-out";
    return accessState.value;
  }

  signedInAs.value = account.username ?? "";
  signedInName.value = account.name ?? "";

  let token = "";
  try {
    const res = await app.acquireTokenSilent({
      account,
      scopes: [import.meta.env.VITE_ENTRA_SCOPE ?? ""],
    });
    token = res.accessToken;
  } catch {
    // We have an account but cannot get a token without interaction. That is
    // a sign-in problem, not an entitlement one — show the sign-in path.
    gateReason.value = "token_interaction";
    accessState.value = "signed-out";
    return accessState.value;
  }

  accessState.value = (await isEntitled(token)) ? "ok" : "not-entitled";
  return accessState.value;
}
